import { fabricEnv, isFabricDev } from '@/src/constants/env';
import { useAuthStatus, useAuthUser } from '@/src/hooks/auth';
import { isInMobile } from '@/src/hooks/mobile';
import { useIsRevenueCatAvailable } from '@/src/modules/pricing/hooks/useIsRevenueCatAvailable';
import { captureException } from '@fabric-app/compat/capabilities/sentry';
import { ensureError, isObject, isString } from '@fabric-app/utils/guards';
import { LOG_LEVEL, Purchases } from '@revenuecat/purchases-capacitor';
import { useCallback } from 'react';

const iOSApiKey = process.env.NEXT_PUBLIC_REVENUECAT_IOS_API_KEY;
const androidApiKey = process.env.NEXT_PUBLIC_REVENUECAT_ANDROID_API_KEY;

/**
 * Configures the RevenueCat plugin with the correct user ID and environment attributes.
 * Should always be called before making any purchases, to ensure the plugin is correctly configured.
 * @returns A function that configures the RevenueCat plugin and returns a boolean indicating if it was successful, when unsuccessful payments should be disabled.
 */
const useConfigureRevenueCat = () => {
  const user = useAuthUser();
  const authStatus = useAuthStatus();
  const isRevenueCatAvailable = useIsRevenueCatAvailable();

  return useCallback(async () => {
    // Running it inside a promise to then set the environment attributes
    const result = await new Promise<boolean>((resolve, reject) => {
      if (!isRevenueCatAvailable) return resolve(false);
      if (!iOSApiKey || !androidApiKey) return resolve(false);

      // If the user is not authenticated, log out and return false, to prevent possible purchases
      if (!user || authStatus !== 'authenticated') {
        return resolve(false);
      }

      const configure = async () => {
        await Purchases.setLogLevel({ level: isFabricDev ? LOG_LEVEL.DEBUG : LOG_LEVEL.INFO });
        await Purchases.configure({
          apiKey: isInMobile('android') ? androidApiKey : iOSApiKey,
          appUserID: user.id,
        });
      };

      Purchases.isConfigured()
        .then(({ isConfigured }) => {
          if (!isConfigured) {
            // If the plugin is not configured, configure it

            return configure()
              .then(() => resolve(true))
              .catch(reject);
          }

          Purchases.getAppUserID()
            .then((configuredAppUserID) => {
              // If the plugin is already configured and has the correct user ID, return true
              if (configuredAppUserID.appUserID === user.id) return resolve(true);

              Purchases.logIn({ appUserID: user.id })
                .then(() => {
                  return resolve(true);
                })
                .catch(reject);
            })
            .catch((error) => {
              if (
                isObject(error) &&
                'message' in error &&
                isString(error.message) &&
                error.message.includes('must be configured')
              ) {
                // configure the plugin
                return configure()
                  .then(() => resolve(true))
                  .catch(reject);
              }

              return reject(error);
            });
        })
        .catch((error) => {
          captureException(ensureError(error));
          configure()
            .then(() => resolve(true))
            .catch(reject);
        });
    });

    // Ensure environment attributes are set always
    Purchases.setAttributes({
      environment: fabricEnv,
    });

    return result;
  }, [authStatus, user, isRevenueCatAvailable]);
};

export default useConfigureRevenueCat;
